import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SVGOverlay, useMap } from 'react-leaflet';
import L from 'leaflet';
import { v4 as uuidv4 } from 'uuid';

import {
  addEdge,
  addNode,
  activeImageOrDuplicateOfSelector,
  currentLayerNodesSelector,
  drawingFromNodeIdSelector,
  editModeSelector,
  effectiveLabelStyleSelector,
  enabledImagesSelector,
  imageBounds,
  ImageLayerType,
  isEditingSelector,
  isSelectingWaypointsSelector,
  labelPositionsSelector,
  LabelPositionType,
  moveLabelInImage,
  OverlayStyle,
  OverlayType,
  overlayLabelsSelector,
  overlaysSelector,
  placementIdToSpaceIdSelector,
  selectedOverlayIdSelector,
  selectedOverlayIdsSelector,
  setDrawingFromNodeId,
  setActiveImage,
  setEditedSpace,
  setEditedSpaceId,
  setMarkerEditingSpaceId,
  setSelectedOverlayId,
  toggleSelectedOverlayId,
  spacePositionsSelector,
  spacesSelector,
  mapSpacesSelector,
  useAppDispatch,
  useAppSelector,
} from '../../../../store';
import { LeafletContext } from '../../../../App';
import { labelText } from '../../../../utils/labelText';
import { OverlayPoint, overlayShapeStyle, polygonCentroid } from '../../../../utils/overlays';
import { Marker } from '../../../labels/Map/Labels/Marker';
import { MAP_ZOOM_FACTOR_VAR } from '../../../labels/shared/labelBadge';
import { OverlayEditing } from './OverlayEditing';

import './Overlays.scss';

export interface OverlayStylePreview {
  id: string;
  style: OverlayStyle;
  // Rotation of its label badge
  rotation?: number;
  // Name of its label being typed, also showing a label not created yet
  name?: string;
  // Other selected overlays, previewing the changed style attributes, and the
  // rotation when changed
  others?: { ids: string[]; changes: OverlayStyle; rotation?: number };
}

// Delay (ms) before a hover ends
const HOVER_END_DELAY = 150;

// Id of label badges previewing a label not created yet
const NEW_LABEL_ID = 'newLabel';

// SVG polygon points in image pixels (matching the viewBox), from the image top-left
function svgPoints(map: L.Map, topLeft: L.Point, points: OverlayType['points']) {
  const zoom = map.getMaxZoom();
  return points.map((point) => {
    const projected = map.project([point.x, point.y], zoom);
    return `${projected.x - topLeft.x},${projected.y - topLeft.y}`;
  }).join(' ');
}

// Polygon centroid seen from its label (top-left corner), in the label's own
// unscaled frame: hovered labels scaled down with the map grow away from it
function labelZoomOrigin(map: L.Map, overlay: OverlayType, position: OverlayPoint, rotation: number, scaleZoom: number) {
  const centroid = polygonCentroid(overlay.points);
  const from = map.project([position.x, position.y], scaleZoom);
  const to = map.project([centroid.x, centroid.y], scaleZoom);
  const angle = (rotation * Math.PI) / 180;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return {
    x: Math.round((dx * Math.cos(angle)) + (dy * Math.sin(angle))),
    y: Math.round((dy * Math.cos(angle)) - (dx * Math.sin(angle))),
  };
}

export function Overlays() {
  const dispatch = useAppDispatch();
  const map = useMap();
  const image = useAppSelector(activeImageOrDuplicateOfSelector);
  const overlays = useAppSelector(overlaysSelector);
  const overlayLabels = useAppSelector(overlayLabelsSelector);
  const getEffectiveStyle = useAppSelector(effectiveLabelStyleSelector);
  const labelPositions = useAppSelector(labelPositionsSelector);
  const isEditing = useAppSelector(isEditingSelector);
  const editMode = useAppSelector(editModeSelector);
  const selectedOverlayId = useAppSelector(selectedOverlayIdSelector);
  const selectedOverlayIds = useAppSelector(selectedOverlayIdsSelector);
  const pathNodes = useAppSelector(currentLayerNodesSelector);
  const drawingFromNodeId = useAppSelector(drawingFromNodeIdSelector);
  const placementIdToSpaceId = useAppSelector(placementIdToSpaceIdSelector);
  // Spaces shown on the map: overlays of the hidden ones hide like their markers
  const mapSpaces = useAppSelector(mapSpacesSelector);
  const spaces = useAppSelector(spacesSelector);
  const spacePositions = useAppSelector(spacePositionsSelector);
  const enabledImages = useAppSelector(enabledImagesSelector);
  const isSelectingWaypoints = useAppSelector(isSelectingWaypointsSelector);
  // Space hovered in the sidebar list (or its pin)
  const { hoveredMarker, activeMarkerRef } = useContext(LeafletContext);

  const [bounds, setBounds] = useState<L.LatLngBounds | null>(null);
  const [hoveredOverlayId, setHoveredOverlayId] = useState<string | null>(null);
  // Hover changes re-render the dragged label, ending its drag, so they wait for its drop
  const isDraggingLabel = useRef(false);
  // Hover ends a bit later, so the pointer can move from the polygon to its label
  const hoverEndTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(hoverEndTimer.current), []);
  const [stylePreview, setStylePreview] = useState<OverlayStylePreview | null>(null);
  // Where the label not created yet of an overlay was dragged to
  const [newLabelPosition, setNewLabelPosition] = useState<{ overlayId: string; position: OverlayPoint } | null>(null);

  const imageLayer = image?.type === 'Image' ? image as ImageLayerType : null;
  const isEditingOverlays = isEditing && editMode === 'overlay';

  useEffect(() => {
    if (!imageLayer) {
      setBounds(null);
      return;
    }
    const frame = requestAnimationFrame(() => setBounds(imageBounds(map, imageLayer)));
    return () => cancelAnimationFrame(frame);
  }, [imageLayer, map]);

  // Zoom factor of the labels scaling with the map, set to the target zoom when
  // a zoom animation starts so they scale along with it
  useEffect(() => {
    const container = map.getContainer();
    const setZoomFactor = (zoom: number) => container.style.setProperty(MAP_ZOOM_FACTOR_VAR, String(2 ** zoom));
    const onZoom = () => setZoomFactor(map.getZoom());
    const onZoomAnim = (event: L.ZoomAnimEvent) => setZoomFactor(event.zoom);
    onZoom();
    map.on('zoom', onZoom);
    map.on('zoomanim', onZoomAnim);
    return () => {
      map.off('zoom', onZoom);
      map.off('zoomanim', onZoomAnim);
      container.style.removeProperty(MAP_ZOOM_FACTOR_VAR);
    };
  }, [map]);

  const findSpaceNode = useCallback((spaceId: string) => pathNodes.find((node) => node.spaceId && (
    String(node.spaceId) === spaceId || placementIdToSpaceId[String(node.spaceId)] === spaceId
  )), [pathNodes, placementIdToSpaceId]);

  // Connect overlays while drawing paths: overlays with a space use the space
  // node, the others get their own node at the overlay centroid
  const handlePathClick = useCallback((overlay: OverlayType) => {
    if (!imageLayer) return;

    const existingNode = overlay.spaceId
      ? findSpaceNode(overlay.spaceId)
      : pathNodes.find((node) => node.overlayId === overlay.id);
    const nodeId = existingNode?.id ?? uuidv4();
    if (!existingNode) {
      dispatch(addNode({
        id: nodeId,
        ...(overlay.spaceId ? { spaceId: overlay.spaceId } : { overlayId: overlay.id }),
        position: polygonCentroid(overlay.points),
        layerId: imageLayer.id,
      }));
    }

    if (!drawingFromNodeId) {
      dispatch(setDrawingFromNodeId(overlay.spaceId ? `space:${overlay.spaceId}` : nodeId));
      return;
    }

    const fromNode = drawingFromNodeId.startsWith('space:')
      ? findSpaceNode(drawingFromNodeId.slice('space:'.length))
      : pathNodes.find((node) => node.id === drawingFromNodeId);
    if (fromNode && fromNode.id !== nodeId) {
      dispatch(addEdge({ from: fromNode.id, to: nodeId, bidirectional: true }));
    }
    dispatch(setDrawingFromNodeId(null));
  }, [dispatch, drawingFromNodeId, findSpaceNode, imageLayer, pathNodes]);

  // Same as clicking the marker of the space the overlay replaces: its hidden
  // marker selects waypoints or opens the space details
  const openSpace = useCallback((spaceId: string) => {
    if (!isEditing || isSelectingWaypoints) {
      activeMarkerRef.current[spaceId]?.fire('click');
    } else if (editMode === 'marker') {
      dispatch(setMarkerEditingSpaceId(spaceId));
    } else {
      const position = spacePositions[spaceId];
      if (!position) return;
      dispatch(setEditedSpace({
        id: spaceId,
        position: { lat: +position.x, lng: +position.y, layerId: position.layerId },
      }));
    }
  }, [activeMarkerRef, dispatch, editMode, isEditing, isSelectingWaypoints, spacePositions]);

  // Names of the layers overlays can open: the enabled ones
  const layerNames = useMemo(
    () => new Map(enabledImages.map((layer) => [layer.id, layer.name])),
    [enabledImages],
  );
  const linkedLayerId = useCallback(
    (overlay: OverlayType) => (overlay.layerId && layerNames.has(overlay.layerId) ? overlay.layerId : null),
    [layerNames],
  );

  // Same as picking the layer in the layer switcher
  const openLayer = useCallback((layerId: number) => {
    dispatch(setActiveImage(layerId));
    if (isEditing) {
      dispatch(setEditedSpaceId(null));
      dispatch(setMarkerEditingSpaceId(null));
    }
  }, [dispatch, isEditing]);

  // Clicks with Ctrl/Cmd/Shift add or remove overlays from the edited ones
  const handleOverlayClick = useCallback((overlay: OverlayType, event?: MouseEvent) => {
    if (isEditingOverlays) {
      const toggle = event?.ctrlKey || event?.metaKey || event?.shiftKey;
      dispatch(toggle ? toggleSelectedOverlayId(overlay.id) : setSelectedOverlayId(overlay.id));
    } else if (isEditing && editMode === 'drawingPath') {
      handlePathClick(overlay);
    } else if (overlay.spaceId) {
      openSpace(overlay.spaceId);
    } else {
      const layerId = linkedLayerId(overlay);
      if (layerId) openLayer(layerId);
    }
  }, [dispatch, editMode, handlePathClick, isEditing, isEditingOverlays, linkedLayerId, openLayer, openSpace]);

  if (!imageLayer) return null;

  const findOverlay = (target: EventTarget | null) => {
    const id = target instanceof Element
      ? target.closest('[data-overlay-id]')?.getAttribute('data-overlay-id')
      : null;
    return overlays.find((overlay) => overlay.id === id);
  };

  const labelsById = new Map(overlayLabels.map((label) => [label.id, label]));
  const mapSpaceIds = new Set(mapSpaces.map((space) => space.id));
  const spaceNames = new Map(spaces.map((space) => [space.id, space.name]));
  // All overlays stay editable in overlay edit mode
  const shownOverlays = isEditingOverlays
    ? overlays
    : overlays.filter((overlay) => !overlay.spaceId || mapSpaceIds.has(overlay.spaceId));
  const views = shownOverlays.map((overlay) => {
    const preview = stylePreview?.id === overlay.id ? stylePreview : null;
    // Edited along with the previewed overlay
    const otherPreview = stylePreview?.others?.ids.includes(overlay.id) ? stylePreview.others : null;
    // Saved label style, or the one being edited
    const labelStyle: OverlayStyle = { ...overlay.style, ...(preview?.style ?? otherPreview?.changes) };

    // Saved label, or the one being typed (renamed, removed, or not created yet).
    // Left blank, it shows the name of its space or linked layer
    const savedLabel = overlay.labelId ? labelsById.get(overlay.labelId) : undefined;
    const typedName = preview?.name ?? savedLabel?.name ?? '';
    const layerId = linkedLayerId(overlay);
    const linkName = overlay.spaceId ? spaceNames.get(overlay.spaceId) : (layerId ? layerNames.get(layerId) : undefined);
    const name = labelText(typedName) ? typedName : linkName;
    const label = name ? { ...(savedLabel ?? { id: NEW_LABEL_ID }), name } : undefined;
    const isNewLabel = !!label && !savedLabel;
    const labelPosition: LabelPositionType | undefined = isNewLabel
      ? (newLabelPosition?.overlayId === overlay.id ? newLabelPosition.position : polygonCentroid(overlay.points))
      : savedLabel && labelPositions[savedLabel.id];

    return {
      overlay,
      label,
      isNewLabel,
      labelPosition,
      labelStyle,
      labelRotation: preview ? preview.rotation : otherPreview?.rotation,
      // Polygon look, from the label style over the map default one
      style: overlayShapeStyle({
        ...getEffectiveStyle({ id: overlay.id, name: '' }),
        ...labelStyle,
      }),
      // Hovered on the map (overlay or its label), or its space in the sidebar
      isHovered: hoveredOverlayId === overlay.id || (!!overlay.spaceId && hoveredMarker === overlay.spaceId),
      isSelected: selectedOverlayIds.includes(overlay.id),
      // The one in the overlay editor
      isEdited: overlay.id === selectedOverlayId,
      onHoverStart: () => {
        if (isDraggingLabel.current) return;
        window.clearTimeout(hoverEndTimer.current);
        setHoveredOverlayId(overlay.id);
      },
      onHoverEnd: () => {
        if (isDraggingLabel.current) return;
        window.clearTimeout(hoverEndTimer.current);
        hoverEndTimer.current = window.setTimeout(() => {
          setHoveredOverlayId((current) => current === overlay.id ? null : current);
        }, HOVER_END_DELAY);
      },
    };
  });

  const topLeft = bounds && map.project(bounds.getNorthWest(), map.getMaxZoom());
  const isEditingOverlayLabel = isEditingOverlays && !!selectedOverlayId;

  return (
    <>
      {bounds && views.length > 0 && (
        <SVGOverlay
          bounds={bounds}
          attributes={{
            viewBox: `0 0 ${imageLayer.width} ${imageLayer.height}`,
            preserveAspectRatio: 'none',
            class: 'image-overlays-layer',
          }}
          interactive={true}
          eventHandlers={{
            // Leaflet handlers (instead of React ones) so the click doesn't reach the map
            click: (event) => {
              const overlay = findOverlay(event.originalEvent.target);
              if (!overlay) return;
              L.DomEvent.stopPropagation(event);
              handleOverlayClick(overlay, event.originalEvent);
            },
          }}
        >
          {views.map(({ overlay, label, style, isHovered, isSelected, onHoverStart, onHoverEnd }) => {
            const { isNeon } = style;
            const isClickable = isEditingOverlays
              || (isEditing && editMode === 'drawingPath')
              || !!overlay.spaceId
              || !!linkedLayerId(overlay);

            return (
              <polygon
                key={overlay.id}
                data-overlay-id={overlay.id}
                data-testid="overlay-polygon"
                aria-label={label && labelText(label.name)}
                className={[
                  'image-overlay',
                  isNeon && 'image-overlay--neon',
                  isSelected && 'image-overlay--selected',
                  isHovered && 'image-overlay--hover',
                  isClickable && 'image-overlay--clickable',
                ].filter(Boolean).join(' ')}
                points={svgPoints(map, topLeft!, overlay.points)}
                fill={style.fillColor}
                fillOpacity={style.fillOpacity}
                stroke={style.strokeColor}
                strokeWidth={style.strokeWidth}
                style={{
                  // Neon glow colour
                  color: style.glowColor,
                  '--overlay-highlighted-fill-opacity': style.highlightedFillOpacity,
                } as React.CSSProperties}
                onMouseEnter={onHoverStart}
                onMouseLeave={onHoverEnd}
              >
                {label && <title>{labelText(label.name)}</title>}
              </polygon>
            );
          })}
        </SVGOverlay>
      )}
      {views.map(({
        overlay, label, isNewLabel, labelPosition: position, labelStyle, labelRotation,
        isHovered, isEdited, onHoverStart, onHoverEnd,
      }) => {
        // Labels show on hover, or always when set, and all of them while an overlay is edited
        if (!label || !position || !(isHovered || isEditingOverlayLabel || labelStyle.alwaysShowLabel)) return null;
        const rotation = labelRotation ?? position.rotation ?? 0;

        return (
          <Marker
            key={`${overlay.id}-${label.id}`}
            label={label}
            position={labelRotation === undefined ? position : { ...position, rotation: labelRotation }}
            isHighlighted={isHovered}
            // The edited label over the others
            isOnTop={isEdited}
            draggable={isEditingOverlays && isEdited}
            styleOverride={labelStyle}
            scaleZoom={labelStyle.scaleZoom}
            zoomOrigin={labelStyle.scaleZoom === undefined
              ? undefined
              : labelZoomOrigin(map, overlay, position, Number(rotation), labelStyle.scaleZoom)}
            handlers={{
              click: (event) => {
                L.DomEvent.stopPropagation(event);
                handleOverlayClick(overlay, (event as L.LeafletMouseEvent).originalEvent);
              },
              mouseover: onHoverStart,
              mouseout: onHoverEnd,
              dragstart: () => {
                isDraggingLabel.current = true;
              },
              dragend: (event) => {
                isDraggingLabel.current = false;
                const { lat, lng } = (event.target as L.Marker).getLatLng();
                if (isNewLabel) {
                  setNewLabelPosition({ overlayId: overlay.id, position: { x: lat, y: lng } });
                } else {
                  dispatch(moveLabelInImage({ id: label.id, x: lat, y: lng }));
                }
              },
            }}
          />
        );
      })}
      {isEditingOverlays && (
        <OverlayEditing
          onStylePreviewChange={setStylePreview}
          newLabelPosition={newLabelPosition?.overlayId === selectedOverlayId ? newLabelPosition.position : null}
        />
      )}
    </>
  );
}
